import { usePostHog } from "@posthog/react";
import { Skeleton } from "@web/components/ui/skeleton";
import { useLanguage } from "@web/lib/contexts/language-context";
import { useConsultation } from "@web/lib/hooks/consultations";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

export function ReadQRCodeTutorial() {
	const { t, language } = useLanguage();
	const navigate = useNavigate();
	const { query } = useParams();
	const { data, isLoading } = useConsultation(query as string);
	const [isAvailable, setIsAvailable] = useState(false);
	const posthog = usePostHog();

	const handleGoToCamera = () => {
		let err: string | null = null;
		try {
			navigate(`/camera?selo=${query}`);
		} catch (error: any) {
			err = error?.message;
			toast.error("Erro ao navegar para a câmera. Por favor, tente novamente.");
		} finally {
			posthog.capture("go_to_camera", {
				queryId: query,
				language,
				error: err,
			});
		}
	};

	useEffect(() => {
		if (!data) return;

		const hasPhoto = data.queryEntries?.some(
			(entry) => entry.type === "PHOTO" && entry?.photo?.length,
		);

		if (hasPhoto) {
			navigate(`/consulta/${data.id}`);
			return;
		}

		setIsAvailable(true);
	}, [data]);

	if (isLoading || !isAvailable) {
		return (
			<div className="flex gap-2 flex-col items-center px-6 py-8 text-center bg-white">
				<div className="flex flex-col gap-2 items-center">
					<Skeleton className="h-6 w-52 rounded-md" />
					<Skeleton className="h-4 w-64 rounded-md" />
					<Skeleton className="h-4 w-56 rounded-md" />
				</div>
				<div className="flex flex-col gap-12 justify-between w-full items-center">
					<Skeleton className="w-full max-w-xs h-64 rounded-xl" />
					<Skeleton className="w-full max-w-sm h-12 rounded-2xl" />
				</div>
			</div>
		);
	}

	return (
		<div className="flex gap-2 flex-col items-center px-6 py-8 text-center bg-white">
			<div className="flex flex-col items-center justify-center gap-2">
				<motion.h2
					initial={{ opacity: 0, y: 16 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.45 }}
					className="max-w-xs text-2xl font-bold leading-snug text-gray-900"
				>
					{t.tutorial.qrTutorial.title}
				</motion.h2>

				<motion.p
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.45, delay: 0.1 }}
					className="max-w-75 text-sm text-center leading-relaxed text-gray-500"
				>
					{t.tutorial.qrTutorial.description}
				</motion.p>
			</div>

			<div className="flex flex-col items-center gap-12 justify-between">
				<motion.div
					initial={{ opacity: 0, scale: 0.95 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.5, delay: 0.15 }}
					className="flex w-full max-w-xs items-center justify-center"
				>
					<img
						src={data?.product?.gifTutorial || ""}
						alt="Produto com selo de autenticidade"
						className="w-full drop-shadow-xl"
					/>
				</motion.div>

				<div className="flex flex-col pl-5 items-start gap-2 justify-start">
					<motion.p
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45, delay: 0.2 }}
						className="text-base text-start text-black"
					>
						1 - {t.tutorial.photoStep.step1}
					</motion.p>
					<motion.p
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45, delay: 0.2 }}
						className="text-base text-start text-black"
					>
						2 - {t.tutorial.photoStep.step2}
					</motion.p>
					<motion.p
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45, delay: 0.2 }}
						className="text-base text-start text-black"
					>
						3 - {t.tutorial.photoStep.step3}
					</motion.p>
					<motion.p
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45, delay: 0.2 }}
						className="text-base text-start text-black"
					>
						4 - {t.tutorial.photoStep.step4}
					</motion.p>
					<motion.p
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45, delay: 0.2 }}
						className="text-base text-start text-black"
					>
						5 - {t.tutorial.photoStep.step5}
					</motion.p>
				</div>

				<motion.button
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.45, delay: 0.25 }}
					whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.97 }}
					className="w-full max-w-sm rounded-2xl bg-[#3B1F6E] cursor-pointer px-5 py-4 text-sm font-semibold tracking-wide text-white shadow-lg shadow-[#3B1F6E]/30"
					onClick={handleGoToCamera}
				>
					{t.tutorial.qrTutorial.button}
				</motion.button>
			</div>
		</div>
	);
}

export default ReadQRCodeTutorial;
